import { ChevronDownIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

import { JsonLd } from "../components/common/JsonLd";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { calculateYears } from "../utils/utils";
import { PATH_ABOUT } from "../utils/constants";
import teamData from "../data/pages/team.json";
import { buildTeamPageGraph } from "../utils/structured-data";

interface Training {
  title: string;
  location?: string;
  date: string;
}

interface TrainingCategory {
  categoryName?: string;
  trainings: Training[];
}

interface Highlight {
  title: string;
  location?: string;
  date?: string;
  ratio?: string;
  image?: string;
  href?: string;
}

interface MemberBlock {
  type: "member";
  name: string;
  imageSrc: string;
  content: string;
  trainings: TrainingCategory[];
  brightness?: string;
  role?: string;
  tenureStartDate?: string;
}

interface HighlightsBlock {
  type: "highlights";
  bannerHref: string;
  items: Highlight[];
}

type TeamBlock = MemberBlock | HighlightsBlock | { type: "groupPhoto"; src: string; aspect: string };

const TrainingCard: React.FC<Training> = ({ title, location, date }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-lg px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-200">
    <div className="min-w-0">
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-snug">{title}</p>
      {location && <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">{location}</p>}
    </div>
    {date && (
      <span className="text-xs text-gray-500 dark:text-gray-500 flex-shrink-0">{date}</span>
    )}
  </div>
);

const TrainingCategoryComponent: React.FC<TrainingCategory> = ({ categoryName, trainings }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const panelId = `training-panel-${(categoryName || "category").replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        className="w-full flex items-center justify-between px-5 py-4 text-left group min-h-11
          hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-ergo-600 dark:group-hover:text-ergo-400 transition-colors duration-200">
            {categoryName}
          </span>
          <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/60 rounded-full px-2 py-0.5">
            {trainings.length}
          </span>
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      <div
        id={panelId}
        aria-hidden={!isExpanded}
        className={`transition-all duration-300 ease-in-out ${isExpanded ? "opacity-100 max-h-[2000px]" : "opacity-0 max-h-0 overflow-hidden"}`}
      >
        <div className="px-5 pb-5 space-y-1">
          {trainings.map((training, index) => (
            <TrainingCard key={index} {...training} />
          ))}
        </div>
      </div>
    </div>
  );
};

const HighlightCard: React.FC<Highlight> = ({ title, location, date, image, href }) => {
  const cardClassName =
    "group block rounded-xl bg-white dark:bg-gray-800/60 border border-gray-200/60 dark:border-gray-700/40 overflow-hidden " +
    "hover:border-gray-300 dark:hover:border-gray-600 transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ergo-500";

  const inner = (
    <>
      {image && (
        <div className="aspect-[4/3] overflow-hidden">
          <img
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            src={process.env.PUBLIC_URL + image}
            alt={title}
          />
        </div>
      )}
      <div className="p-4">
        <h3 className="text-sm leading-snug font-medium text-gray-700 dark:text-gray-300 group-hover:text-ergo-600 dark:group-hover:text-ergo-400 transition-colors duration-300">
          {title}
        </h3>
        <div className="mt-1.5 flex text-xs text-gray-500 dark:text-gray-500">
          {location && <p className="leading-4 after:content-[','] after:mr-1">{location}</p>}
          <p className="leading-4">{date}</p>
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cardClassName}>
        {inner}
      </a>
    );
  }

  return <div className={cardClassName}>{inner}</div>;
};

const TeamMemberPhoto: React.FC<{ src: string; className?: string }> = ({ src, className = "" }) => (
  <div className="group relative">
    <div
      className={`aspect-[3/4] rounded-2xl overflow-hidden shadow-sm bg-white dark:bg-gray-800 max-w-[300px] ${className}`}
    >
      <img
        src={process.env.PUBLIC_URL + src}
        alt="Team der Ergotherapie Rottweil"
        className="w-full h-full object-cover"
      />
    </div>
  </div>
);

function renderMemberContent(content: string, tenureStartDate?: string): string {
  if (tenureStartDate) {
    return content.replace("{{tenureYears}}", String(calculateYears(tenureStartDate)));
  }
  return content;
}

const TeamMemberSection: React.FC<MemberBlock> = ({
  name,
  imageSrc,
  content,
  trainings,
  brightness = "",
  role,
  tenureStartDate,
}) => (
  <div className="mt-14">
    <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:gap-10">
      <div className="min-w-0 max-w-2xl">
        <div className="mb-4">
          <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">{name}</h3>
          {role && (
            <p className="mt-1 text-sm font-medium text-ergo-600 dark:text-ergo-400">{role}</p>
          )}
        </div>
        <div className="space-y-3 text-base text-gray-500 dark:text-gray-400 leading-relaxed">
          {renderMemberContent(content, tenureStartDate)
            .split("\n\n")
            .map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
        </div>
      </div>
      <img
        src={process.env.PUBLIC_URL + imageSrc}
        alt={`${name} - ${role ?? "Ergotherapeutin, Praxisinhaberin"} bei Ergotherapie Rottweil`}
        className={`mx-auto w-64 shrink-0 md:w-72 lg:mx-0 lg:w-64 xl:w-72 aspect-[3/4] rounded-2xl object-cover shadow-sm ${brightness}`}
      />
    </div>
    {trainings.length > 0 && (
      <div className="mt-10">
        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 tracking-wide mb-3">
          Fort- & Weiterbildungen
        </p>
        <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/40 bg-white dark:bg-gray-800/40 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/40">
          {trainings.map((category, index) => (
            <TrainingCategoryComponent key={index} {...category} />
          ))}
        </div>
      </div>
    )}
  </div>
);

const PromotionBanner: React.FC<{ href: string }> = ({ href }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="block group -mt-6">
    <div
      className="bg-white dark:bg-gray-800/60 border border-gray-200/60 dark:border-gray-700/40 rounded-2xl p-5
      hover:border-ergo-300 dark:hover:border-ergo-700 transition-colors duration-300"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="rounded-xl bg-ergo-50 dark:bg-ergo-900/80 p-3">
            <svg
              className="w-6 h-6 text-ergo-600 dark:text-ergo-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Mehr zu Sexualität auf</p>
            <p className="text-lg font-semibold text-ergo-600 dark:text-ergo-400">sx-med.de</p>
          </div>
        </div>
        <svg
          className="w-5 h-5 text-gray-400 group-hover:text-ergo-600 dark:group-hover:text-ergo-400 transition-colors duration-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  </a>
);

export default function AboutPage() {
  const blocks = teamData.blocks as TeamBlock[];

  return (
    <>
      <SEOHelmet page="about" pathname={PATH_ABOUT} />
      <JsonLd data={buildTeamPageGraph()} />

      <main id="main-content" tabIndex={-1} className="space-y-14">
        <header>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6">
            Unser Team
          </h1>
          <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-10">{teamData.intro}</p>
          <section className="space-y-8">
            <div className="w-full">
              <TeamMemberPhoto
                src={teamData.groupPhoto.src}
                className="!aspect-[16/9] !max-w-full"
              />
            </div>
          </section>
        </header>

        <section>
          <h2 className="sr-only">Team Mitglieder Details</h2>
          {blocks.map((block, index) => {
            if (block.type === "groupPhoto") return null;
            if (block.type === "member") {
              return (
                <article key={block.name}>
                  <TeamMemberSection {...block} />
                </article>
              );
            }
            if (block.type === "highlights") {
              return (
                <section key={`highlights-${index}`} className="space-y-6">
                  <h3 className="sr-only">Highlights und Publikationen</h3>
                  <div className="mx-auto">
                    <PromotionBanner href={block.bannerHref} />
                  </div>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {block.items.map((highlight, hi) => (
                      <HighlightCard key={hi} {...highlight} />
                    ))}
                  </div>
                </section>
              );
            }
            return null;
          })}
        </section>
      </main>
    </>
  );
}
